import React from 'react';
import { Link } from 'react-router-dom';
import BrandMark from 'js/components/elements/brand/BrandMark';
import { ROUTE_DASHBOARD } from 'js/constants/pathnames';

interface FlowErrorViewProps {
  error: string;
  flowId: string;
  baseUrl: string;
  isEmbeddedMode: boolean;
}

export function FlowErrorView({ error, flowId, baseUrl, isEmbeddedMode }: FlowErrorViewProps) {
  const styles = {
    rootContainer: {
      height: '100%',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(243, 244, 246, 1)',
      fontSize: '16px',
    },
    contentContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      padding: '30px',
    },
    logoOuterContainer: {
      marginRight: '24px',
    },
    logoInnerContainer: {
      border: '1px solid #ff7436',
      borderRadius: '0.375em',
      background: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '5em',
      height: '5em',
      padding: '1em',
      boxShadow:
        'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    },
    textContainer: {
      paddingLeft: '24px',
      borderLeft: '1px solid #ff7436',
    },
    heading: {
      color: '#ff7436',
      fontSize: '1.5em',
      fontWeight: 'bold',
      marginBottom: '8px',
    },
    subheading: {
      fontSize: '1em',
      marginBottom: '16px',
    },
    flowIdText: {
      fontSize: '0.875em',
      color: '#6b7280',
      marginBottom: '16px',
      fontFamily: 'monospace',
    },
    buttonCta: {
      display: 'inline-block',
      margin: '1.5em 1em 0 0',
      padding: '0.5em 0.75em',
      borderRadius: '0.5em',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
      fontWeight: 500,
      fontSize: '0.75em',
      lineHeight: '1em',
      cursor: 'pointer',
      border: '1px solid #357bff',
      color: '#357bff',
      background: 'transparent',
      transition: 'all 0.2s ease',
    },
    buttonGray: {
      border: '1px solid rgba(75, 85, 99, 1)',
      color: 'rgba(75, 85, 99, 1)',
    },
  };

  const getErrorContext = () => {
    if (error.includes('not found')) {
      return {
        title: 'Flow Not Found',
        message: "The flow you're looking for may have been deleted or moved.",
        helpText:
          'Check with your team administrator if you believe you should have access to this flow.',
      };
    }
    if (error.includes('permissions')) {
      return {
        title: 'Access Denied',
        message: "You don't have the necessary permissions to access this flow.",
        helpText: 'Contact your administrator to request access to this flow.',
      };
    }
    return {
      title: 'Unable to Load Flow',
      message: 'There was an issue loading the flow data. This might be a temporary problem.',
      helpText: 'Try refreshing the page or contact support if the problem persists.',
    };
  };

  const errorContext = getErrorContext();

  return (
    <div style={styles.rootContainer}>
      <div style={styles.contentContainer} data-testid="flow-error-screen">
        <div style={styles.logoOuterContainer}>
          <div style={styles.logoInnerContainer}>
            <BrandMark animated={true} />
          </div>
        </div>

        <div style={styles.textContainer}>
          <div style={styles.flowIdText}>Flow ID: {flowId}</div>
          <h1 style={styles.heading}>{errorContext.title}</h1>
          <p style={styles.subheading}>{errorContext.message}</p>
          {isEmbeddedMode ? (
            <div>
              <button onClick={() => window.location.reload()} style={styles.buttonCta}>
                Reload the page
              </button>
            </div>
          ) : (
            <div>
              <Link to={baseUrl}>
                <button style={styles.buttonCta}>&larr; Back to Flows</button>
              </Link>
              <Link to={ROUTE_DASHBOARD}>
                <button style={{ ...styles.buttonCta, ...styles.buttonGray }}>
                  &larr; Back to Home
                </button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
